import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Input,
  Stack,
  Divider,
  Avatar
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonIcon from '@mui/icons-material/Person';

import MomentsUser from "../../components/MomentsUser";

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("❌ Erro no MomentsUser:", error, errorInfo);
    this.setState({ error: error });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error" icon={<ErrorOutlineIcon />}>
          <Typography variant="h6" gutterBottom>
            Erro ao carregar atividades
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            {this.state.error && this.state.error.toString()}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Verifique se há dados disponíveis ou tente recarregar a página.
          </Typography>
        </Alert>
      );
    }

    return this.props.children;
  }
}

const ChatMoments = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();
  
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  const [isLoading, setIsLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const { socket, user } = useContext(AuthContext);
  const companyId = user.companyId;

  useEffect(() => {
    console.log("✅ ChatMoments montado");
    
    const fetchData = async () => {
      try {
        const { data } = await api.get("/usersMoments");
        setTickets(data);
        
        const users = data.reduce((acc, ticket) => {
          if (ticket.user && !acc.find(u => u.id === ticket.user.id)) {
            acc.push({
              id: ticket.user.id,
              name: ticket.user.name
            });
          }
          return acc;
        }, []);
        
        setAvailableUsers(users);
        setIsLoading(false);
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        toastError(error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const onAppMessage = (data) => {
      if (data.action === "create" || data.action === "update" || data.action === "delete") {
        fetchTickets();
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket, companyId]);

  const fetchTickets = async () => {
    try {
      const { data } = await api.get("/usersMoments");
      setTickets(data);
      
      const users = data.reduce((acc, ticket) => {
        if (ticket.user && !acc.find(u => u.id === ticket.user.id)) {
          acc.push({
            id: ticket.user.id,
            name: ticket.user.name
          });
        }
        return acc;
      }, []);
      
      setAvailableUsers(users);
    } catch (err) {
      toastError(err);
    }
  };

  const handleUserChange = (event) => {
    const value = event.target.value;
    setSelectedUsers(typeof value === 'string' ? value.split(',') : value);
  };

  const handleDeleteChip = (userToDelete) => {
    setSelectedUsers((users) => users.filter((user) => user !== userToDelete));
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 300,
        borderRadius: 8,
      },
    },
    getContentAnchorEl: null,
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">
        
        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                Tempo Real
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Atividades e interações ao vivo
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
              {new Date().toLocaleDateString('pt-BR', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              }).replace(/^\w/, c => c.toUpperCase())}
            </Typography>
          </Box>
        </Stack>

        {/* Seção de Filtros */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
              Filtrar por Usuários
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <FormControl fullWidth size="small">
              <Select
                multiple
                displayEmpty
                value={selectedUsers}
                onChange={handleUserChange}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <span style={{ color: "#999999", fontSize: "0.875rem" }}>Selecione um ou mais usuários</span>;
                  }
                  return <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>{selected.length} usuário(s) selecionado(s)</span>;
                }}
                MenuProps={MenuProps}
                sx={{
                  borderRadius: 2,
                  fontSize: "0.875rem",
                  height: 40,
                  "& .MuiSelect-select": {
                    py: 1.25,
                    display: "flex",
                    alignItems: "center"
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider"
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main"
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main"
                  }
                }}
              >
                <MenuItem disabled value="">
                  <em style={{ fontSize: "0.875rem" }}>Selecione os usuários</em>
                </MenuItem>
                {availableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.name}>
                    <Checkbox 
                      checked={selectedUsers.indexOf(user.name) > -1}
                      color="primary"
                      size="small"
                    />
                    <ListItemText 
                      primary={user.name}
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                        fontWeight: 500
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedUsers.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 2.5, flexWrap: "wrap", gap: 1 }}>
                {selectedUsers.map((user) => (
                  <Chip
                    key={user}
                    icon={<PersonIcon sx={{ fontSize: 16 }} />}
                    label={user}
                    onDelete={() => handleDeleteChip(user)}
                    size="small"
                    sx={{
                      bgcolor: "rgba(25, 118, 210, 0.08)",
                      color: PRIMARY_MAIN,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      border: "1px solid",
                      borderColor: "rgba(25, 118, 210, 0.2)",
                      borderRadius: 1.5,
                      "&:hover": {
                        bgcolor: "rgba(25, 118, 210, 0.12)",
                      },
                      "& .MuiChip-deleteIcon": {
                        color: PRIMARY_MAIN,
                        fontSize: 18,
                        "&:hover": {
                          color: PRIMARY_DARK
                        }
                      },
                      "& .MuiChip-icon": {
                        color: PRIMARY_MAIN
                      },
                      transition: "all 0.2s ease"
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Paper>

        {/* Seção de Atividades */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                  Atividades dos Usuários
                </Typography>
                {selectedUsers.length > 0 && (
                  <Chip
                    label={`${selectedUsers.length} filtrado${selectedUsers.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      bgcolor: "action.selected",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      height: 24
                    }}
                  />
                )}
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: 3 }}>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px", flexDirection: "column", gap: 2 }}>
                <CircularProgress sx={{ color: PRIMARY_MAIN }} size={40} />
                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                  Carregando atividades...
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)"
                  },
                  gap: 2.5
                }}
              >
                <ErrorBoundary>
                  <MomentsUser selectedUsers={selectedUsers} tickets={tickets} />
                </ErrorBoundary>
              </Box>
            )}
          </Box>
        </Paper>

      </Container>
    </Box>
  );
};

export default ChatMoments;